import { type ChangeEvent, type KeyboardEvent, type ReactNode, type RefObject, useId } from 'react'

import { astraTwMerge } from '../Utilities/astraTwMerge'
import type { DropdownList } from './dropdownFloating'
import { DROPDOWN_PANEL_PADDING_CLASS, DROPDOWN_SEARCH_INPUT_CLASS, DROPDOWN_SEARCH_LIST_CLASS, DROPDOWN_SEARCH_ROW_CLASS } from './dropdownStyles'

export type DropdownMenuSearch = {
    /**
     * The current query. The field is controlled, and narrowing the list is the consumer's job: only
     * they know what an option matches on — a surgeon's full name behind their initials, a count that
     * should survive filtering — and whether the answer comes from a server.
     */
    value: string
    onChange: (value: string) => void
    /**
     * Placeholder text, which also names the field for a screen reader unless `label` says otherwise.
     */
    placeholder?: string
    /**
     * An accessible name for the field, for when the placeholder is not one.
     */
    label?: string
}

/**
 * Keys the field hands to the menu: the arrows move the mark, which floating-ui does from the panel, Esc
 * dismisses it and Tab leaves it. Everything else it handles itself.
 */
const MENU_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Escape', 'Tab'])

type SearchFieldProps = {
    search: DropdownMenuSearch
    ref: RefObject<HTMLInputElement | null>
    list: DropdownList
    itemIdPrefix: string
    listId: string
}

const DropdownSearchField = ({ search, ref, list, itemIdPrefix, listId }: SearchFieldProps) => {
    const { elementsRef, activeIndex, setActiveIndex } = list

    // The first row of whatever the query leaves takes the mark: Enter should mean the obvious result,
    // not whatever happened to sit at that position before the last keystroke.
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        search.onChange(event.target.value)
        setActiveIndex(0)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (MENU_KEYS.has(event.key)) return

        // ArrowLeft is the caret's while there is text to its left, and the menu's once there is not:
        // in a submenu it closes the panel, which is the only way back out by keyboard.
        const caretAtStart = event.currentTarget.selectionStart === 0 && event.currentTarget.selectionEnd === 0
        if (event.key === 'ArrowLeft' && caretAtStart) return

        // Every other key is the field's: Home and End move the caret rather than the mark, and ArrowLeft
        // has to move the caret rather than close the submenu the field sits in.
        event.stopPropagation()

        if (event.key === 'Enter' && activeIndex !== null) {
            event.preventDefault()
            const active = elementsRef.current[activeIndex]
            // The query may have emptied the list, leaving a stale row behind the mark.
            if (active?.isConnected && active.getAttribute('aria-disabled') !== 'true') active.click()
        }
    }

    return (
        <div className={DROPDOWN_SEARCH_ROW_CLASS}>
            <input
                ref={ref}
                type="text"
                value={search.value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={search.placeholder ?? 'Søk...'}
                aria-label={search.label ?? search.placeholder ?? 'Søk'}
                aria-controls={listId}
                aria-activedescendant={activeIndex === null ? undefined : `${itemIdPrefix}-${activeIndex}`}
                autoComplete="off"
                spellCheck={false}
                className={DROPDOWN_SEARCH_INPUT_CLASS}
            />
        </div>
    )
}

type Props = {
    search: DropdownMenuSearch | undefined
    searchRef: RefObject<HTMLInputElement | null>
    list: DropdownList
    itemIdPrefix: string
    noPadding?: boolean
    children: ReactNode
}

/**
 * A panel's body. Without a search field it is the items themselves, so a menu that does not search
 * keeps the markup it had. With one, the field is pinned above a list that scrolls under it.
 *
 * The list then carries the `menu` role rather than the panel: a menu may only own menu items, and a
 * text field is not one. The panel around the two becomes a dialog.
 */
export const DropdownPanelBody = ({ search, searchRef, list, itemIdPrefix, noPadding, children }: Props) => {
    const listId = useId()

    if (!search) return children

    return (
        <>
            <DropdownSearchField search={search} ref={searchRef} list={list} itemIdPrefix={itemIdPrefix} listId={listId} />
            <div id={listId} role="menu" className={astraTwMerge(DROPDOWN_SEARCH_LIST_CLASS, noPadding ? '' : DROPDOWN_PANEL_PADDING_CLASS)}>
                {children}
            </div>
        </>
    )
}
